import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { HelpCircle, PhoneCall } from 'lucide-react';
import useRequest from './hooks/use-req';
import { showToast } from './toast';
import { BVNIcon, CalendarIcon, GreenThick } from '../assets/icon';
import Input from './input';
import Select from './select';
import Button from './button';
import BVNInfoModal from './bvn-info-modal';
import { DateOfBirthPicker } from './date-of-birth-picker';

interface BVNFormData {
    bvn: string;
    name: string;
    preferred_name?: string;
    phone: string;
    gender: string;
    date_of_birth: string;
}

interface BVNDetails {
    bvn: string;
    name: string;
    phone: string;
    dateOfBirth: string;
}

interface BVNVerificationProps {
    onSuccess?: () => void;
    onSkip?: () => void;
    onClose?: () => void;
    showSkip?: boolean;
    variant?: 'page' | 'modal';
    title?: string;
    description?: string;
    buttonText?: string;
    skipText?: string;
    successButtonText?: string;
    showWarning?: boolean;
    warningText?: string;
}

const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
];

// Maps the possible field-key spellings the API might return errors under
// onto our form's field names, since the exact contract isn't confirmed yet.
const FIELD_ERROR_KEYS: Record<string, keyof BVNFormData> = {
    bvn: 'bvn',
    name: 'name',
    mobile_no: 'phone',
    phone: 'phone',
    phone_number: 'phone',
    date_of_birth: 'date_of_birth',
    dateOfBirth: 'date_of_birth',
    dob: 'date_of_birth',
};

const formatDob = (value: string) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return `${date.getDate()} ${date.toLocaleString('en-US', { month: 'short' })}, ${date.getFullYear()}`;
};

const WarningBanner = ({ text }: { text?: string }) => (
    <div className='flex flex-col bg-[#96A1AC1F] px-5 pt-3 rounded-lg text-[14px]'>
        <span className="text-[#121212] h-[50px] gap-2 flex mt-2">
            <svg width="24" height="24" className='w-[24px] h-[24px]' viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_6356_5560)">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill="#F37A50" />
                </g>
                <defs>
                    <clipPath id="clip0_6356_5560">
                        <rect width="24" height="24" fill="white" />
                    </clipPath>
                </defs>
            </svg>
            {text || 'Reward withdrawals will remain locked until BVN verification is completed.'}
        </span>
    </div>
);

const BenefitsChecklist = () => (
    <ul className="space-y-5 mt-3 mb-6">
        <li className="flex items-start gap-2">
            <GreenThick />
            Access all VouchLinks features
        </li>
        <li className="flex items-start gap-2">
            <GreenThick />
            Withdraw your earned rewards
        </li>
        <li className="flex items-start gap-2">
            <GreenThick />
            Prevent fraudulent activity
        </li>
        <li className="flex items-start gap-2">
            <GreenThick />
            Secure your account
        </li>
    </ul>
);

const BVNVerification: React.FC<BVNVerificationProps> = ({
    onSuccess,
    onSkip,
    onClose,
    showSkip = true,
    variant = 'page',
    title = 'Verify BVN',
    description = variant === 'modal'
        ? "Verify your identity using the details exactly as they appear on your BVN to enable reward withdrawals."
        : "Verify your identity using the details exactly as they appear on your BVN to access active reward programs.",
    buttonText = 'Verify my BVN',
    skipText = 'Skip Step',
    successButtonText = 'Save and Proceed',
    showWarning = true,
    warningText,
}) => {
    const authToken = localStorage.getItem("authToken");
    const storedData = localStorage.getItem("userDetails");
    const userDetails = storedData ? JSON.parse(storedData) : null;

    const [step, setStep] = useState<'form' | 'verified'>('form');
    const [bvnDetails, setBvnDetails] = useState<BVNDetails | null>(null);
    const [showInfoModal, setShowInfoModal] = useState(false);

    const { handleSubmit, control, getValues, setError } = useForm<BVNFormData>({
        defaultValues: {
            bvn: '',
            name: userDetails?.first_name && userDetails?.last_name
                ? `${userDetails.first_name} ${userDetails.last_name}`
                : '',
            preferred_name: '',
            phone: userDetails?.phone || '',
            gender: '',
            date_of_birth: '',
        },
    });

    const { loading, makeRequest } = useRequest('/account/verify-bvn', 'POST', {
        Authorization: `Bearer ${authToken}`,
    });

    const { loading: savingBvn, makeRequest: updateBvn } = useRequest('/account/recommender/profile', 'PUT', {
        Authorization: `Bearer ${authToken}`,
    });

    // Verify BVN + personal details in a single call (no OTP step)
    const handleVerifyBVN = handleSubmit(async (formData) => {
        // Confirmed final payload shape: exactly these 4 fields, nothing else.
        const phoneDigits = formData.phone.replace(/\D/g, ''); // "+2348102814656" -> "2348102814656"
        const mobileNo = phoneDigits.startsWith('234') ? `0${phoneDigits.slice(3)}` : phoneDigits;

        const [response, error] = await makeRequest({
            bvn: formData.bvn,
            name: formData.name,
            date_of_birth: formData.date_of_birth,
            mobile_no: mobileNo,
        });

        if (response?.status) {
            const returned = response?.data || {};
            setBvnDetails({
                bvn: returned.bvn || formData.bvn,
                name: returned.name || formData.name,
                phone: returned.phone || formData.phone,
                dateOfBirth: returned.date_of_birth || returned.dateOfBirth || formData.date_of_birth,
            });
            setStep('verified');
        } else {
            const fieldErrors = error?.errors || error?.data?.errors || error?.field_errors;
            let mapped = false;

            if (fieldErrors && typeof fieldErrors === 'object') {
                Object.entries(fieldErrors).forEach(([key, message]) => {
                    const fieldName = FIELD_ERROR_KEYS[key];
                    if (fieldName) {
                        setError(fieldName, {
                            type: 'server',
                            message: Array.isArray(message) ? String(message[0]) : String(message),
                        });
                        mapped = true;
                    }
                });
            }

            if (!mapped) {
                showToast(error?.message || 'BVN verification failed', false);
            }
        }
    });

    // Save the verified BVN details + remaining editable fields, then proceed
    const handleSaveAndProceed = async () => {
        const { preferred_name, gender } = getValues();
        // `/account/recommender/profile` still expects split first_name/last_name (unconfirmed
        // whether it now accepts a single `name`) — derive them from the verified full name.
        const [firstName, ...rest] = (bvnDetails?.name || '').trim().split(' ');
        const lastName = rest.join(' ');

        const [response, error] = await updateBvn({
            bvn: bvnDetails?.bvn,
            name: bvnDetails?.name,
            first_name: firstName,
            last_name: lastName,
            phone: bvnDetails?.phone,
            date_of_birth: bvnDetails?.dateOfBirth,
            preferred_name,
            gender,
        });

        if (response?.status) {
            onSuccess?.();
        } else {
            showToast(error?.message || 'Failed to save details', false);
        }
    };

    const handleSkip = () => {
        onSkip?.();
    };

    const handleClose = () => {
        onClose?.();
    };

    const content = (
        <div className={variant === 'modal' ? 'p-6' : ''}>
            {variant === 'modal' && onClose && (
                <button
                    onClick={handleClose}
                    className="float-right text-gray-500 hover:text-gray-700"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </button>
            )}

            {step === 'form' && (
                <>
                    <h2 className="text-[22px] font-bold text-gray-900 mb-1">
                        {title}
                    </h2>
                    <p className="text-gray-500 font-medium text-[18px] mb-5">
                        {description}
                    </p>

                    <div className="flex items-center gap-1.5 mb-2">
                        <label className="text-[14px] font-bold text-[#121212]">Bank Verification Number</label>
                        <button
                            type="button"
                            onClick={() => setShowInfoModal(true)}
                            className="text-emerald-600 hover:text-emerald-700"
                            aria-label="About BVN Verification"
                        >
                            <HelpCircle size={16} />
                        </button>
                    </div>
                    <Controller
                        name="bvn"
                        control={control}
                        rules={{ required: "BVN is required" }}
                        render={({ field, fieldState }) => (
                            <Input
                                placeholder="Enter your 11 digit BVN"
                                value={field.value}
                                error={fieldState?.error?.message}
                                onChange={field.onChange}
                                className="w-full mb-3"
                                icon={<BVNIcon />}
                            />
                        )}
                    />

                    <Controller
                        name="name"
                        control={control}
                        rules={{ required: "Name is required" }}
                        render={({ field, fieldState }) => (
                            <Input
                                label="Name"
                                placeholder="Enter your full name as it appears on your BVN"
                                value={field.value}
                                error={fieldState?.error?.message}
                                onChange={field.onChange}
                                className="w-full mb-3"
                            />
                        )}
                    />

                    <Controller
                        name="preferred_name"
                        control={control}
                        render={({ field }) => (
                            <Input
                                label="Preferred Name (Optional)"
                                placeholder="Enter a preferred name"
                                value={field.value}
                                onChange={field.onChange}
                                className="w-full mb-3"
                                subtext="If provided, this name will be displayed on your profile and used in VouchLinks communications."
                            />
                        )}
                    />

                    <Controller
                        name="phone"
                        control={control}
                        rules={{
                            required: "Phone number is required",
                            pattern: {
                                value: /^\+234[0-9]{10}$/,
                                message: "Enter a valid phone number starting with +234 and 10 digits",
                            },
                        }}
                        render={({ field, fieldState }) => (
                            <Input
                                type="tel"
                                label="Phone Number"
                                icon={<PhoneCall />}
                                prefix="+234"
                                value={field.value ? field.value.replace("+234", "") : ""}
                                error={fieldState?.error?.message}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, "");
                                    if (val.length <= 10) {
                                        field.onChange(`+234${val}`);
                                    }
                                }}
                                className="w-full mb-3"
                            />
                        )}
                    />

                    <div className="grid grid-cols-2 gap-3 mt-3 mb-3">
                        <Controller
                            name="gender"
                            control={control}
                            rules={{ required: "Please select a gender" }}
                            render={({ field, fieldState }) => (
                                <Select
                                    label="Gender"
                                    options={genderOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={fieldState?.error?.message}
                                />
                            )}
                        />
                        <Controller
                            name="date_of_birth"
                            control={control}
                            rules={{ required: "Date of birth is required" }}
                            render={({ field, fieldState }) => (
                                <DateOfBirthPicker
                                    label="Date of Birth"
                                    icon={<CalendarIcon />}
                                    value={field.value}
                                    error={fieldState?.error?.message}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </div>

                    <Button
                        onClick={handleVerifyBVN}
                        variant="primary"
                        className="w-full"
                        disabled={loading}
                    >
                        {loading ? 'Verifying...' : buttonText}
                    </Button>

                    {showSkip && (
                        <button
                            onClick={handleSkip}
                            className="text-center my-4 flex gap-1 justify-center font-medium text-sm mb-8 text-gray-500 w-full"
                        >
                            Continue without BVN?
                            <span className="underline font-bold">{skipText}</span>
                        </button>
                    )}

                    {variant !== 'modal' && <BenefitsChecklist />}

                    {variant !== 'modal' && showWarning && <WarningBanner text={warningText} />}
                </>
            )}

            {step === 'verified' && (
                <>
                    <h2 className="text-[22px] font-bold text-gray-900 mb-1">
                        {title}
                    </h2>
                    <p className="text-gray-500 font-medium text-[18px] mb-5">
                        {description}
                    </p>

                    <Input
                        label="Bank Verification Number"
                        value={bvnDetails?.bvn}
                        className="w-full mb-1"
                        icon={<BVNIcon />}
                        readOnly={true}
                        rightIcon={<GreenThick />}
                    />

                    <button
                        type="button"
                        onClick={() => setStep('form')}
                        className="text-sm underline text-gray-500 font-medium mb-4 text-left"
                    >
                        Edit BVN
                    </button>

                    <Input
                        label="Name"
                        value={bvnDetails?.name}
                        readOnly={true}
                        rightIcon={<GreenThick />}
                        className="w-full mb-3"
                    />

                    <Controller
                        name="preferred_name"
                        control={control}
                        render={({ field }) => (
                            <Input
                                label="Preferred Name (Optional)"
                                placeholder="Enter a preferred name"
                                value={field.value}
                                onChange={field.onChange}
                                className="w-full mb-3"
                            />
                        )}
                    />

                    <Input
                        label="Phone Number"
                        value={bvnDetails?.phone}
                        icon={<PhoneCall />}
                        readOnly={true}
                        rightIcon={<GreenThick />}
                        className="w-full mb-3"
                    />

                    <div className="grid grid-cols-2 gap-3 mt-3 mb-5">
                        <Controller
                            name="gender"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label="Gender"
                                    options={genderOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                        <Input
                            label="Date of Birth"
                            value={bvnDetails?.dateOfBirth ? formatDob(bvnDetails.dateOfBirth) : ''}
                            readOnly={true}
                            rightIcon={<GreenThick />}
                        />
                    </div>

                    <Button
                        onClick={handleSaveAndProceed}
                        variant="primary"
                        className="w-full"
                        disabled={savingBvn}
                    >
                        {savingBvn ? 'Saving...' : successButtonText}
                    </Button>

                    {variant !== 'modal' && showWarning && (
                        <div className="mt-4">
                            <WarningBanner text={warningText} />
                        </div>
                    )}
                </>
            )}
        </div>
    );

    return (
        <>
            {variant === 'modal' ? (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
                        {content}
                    </div>
                </div>
            ) : (
                <div className="flex justify-center items-center">
                    <div className="w-full max-w-2xl">
                        {content}
                    </div>
                </div>
            )}

            <BVNInfoModal isOpen={showInfoModal} onClose={() => setShowInfoModal(false)} />
        </>
    );
};

export default BVNVerification;
